import { useCallback, useReducer, useRef } from 'react';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import { apiSearchByNameRetrieve } from '@rover/rsdk/src/apiClient/latest/api-search-by-name-retrieve';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { SearchNameQuerySubmittedEvent, SearchNameResultsRenderedEvent } from '../analyticsEvents';
import { mapToCardResult } from '../api/searchByNameAdapter';
import type { NameSearchResult, SearchByNameAction, SearchByNameState } from '../types';
import {
  getQueryNumberInSession,
  incrementQueryNumberInSession,
} from '../utils/queryNumberInSession';

const initialState: SearchByNameState = {
  query: '',
  results: null,
  searchUuid: null,
  isLoading: false,
  error: null,
};

const GENERIC_SEARCH_ERROR: MessageDescriptor = msg`Something went wrong. Please try again.`;

function reducer(state: SearchByNameState, action: SearchByNameAction): SearchByNameState {
  switch (action.type) {
    case 'SEARCH_START':
      // Keep the prior searchUuid until SEARCH_SUCCESS so mid-re-search card clicks still correlate.
      return {
        ...state,
        query: action.query,
        isLoading: true,
        error: null,
      };
    case 'SEARCH_SUCCESS':
      return {
        ...state,
        results: action.results,
        searchUuid: action.searchUuid,
        isLoading: false,
      };
    case 'SEARCH_ERROR':
      return { ...state, error: action.error, isLoading: false };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

// lat/lng required: the backend rejects requests without a bbox or center+zoom.
export type SearchParams = {
  name: string;
  lat: string;
  lng: string;
  location: string;
  locationZip: string | null;
};

type UseSearchByNameReturn = {
  state: SearchByNameState;
  search: (params: SearchParams) => Promise<void>;
  reset: () => void;
};

export default function useSearchByName(): UseSearchByNameReturn {
  const { i18n } = useLingui();
  const [state, dispatch] = useReducer(reducer, initialState);

  // Drop out-of-order responses when searches overlap.
  const requestIdRef = useRef(0);
  const searchStartRef = useRef<number>(0);

  const search = useCallback(
    async ({ name, lat, lng, location, locationZip }: SearchParams): Promise<void> => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      const startedAt = Date.now();
      searchStartRef.current = startedAt;

      const queryNumberInSession = incrementQueryNumberInSession();
      const searchQueryWordCount = name.trim().split(/\s+/).filter(Boolean).length;
      emitAnalyticsEvent(
        new SearchNameQuerySubmittedEvent({
          searchQuery: name,
          location,
          locationZip,
          searchQueryWordCount,
          searchQueryLength: name.length,
          queryNumberInSession,
        })
      );

      dispatch({ type: 'SEARCH_START', query: name });

      try {
        const response = await apiSearchByNameRetrieve({
          nameQuery: name,
          centerlat: Number(lat),
          centerlng: Number(lng),
        });
        if (requestIdRef.current !== requestId) return;

        const results: NameSearchResult[] = (response.results ?? []).map(mapToCardResult);
        const searchUuid: string | null = response.searchUuid ?? null;

        dispatch({
          type: 'SEARCH_SUCCESS',
          results,
          searchUuid,
        });

        emitAnalyticsEvent(
          new SearchNameResultsRenderedEvent({
            resultCount: results.length,
            isZeroResults: results.length === 0,
            searchUuid,
            // Client-perceived round-trip latency, not server processing time.
            latencyMs: Date.now() - startedAt,
            sitterPersonOpks: results.map((result) => result.personOpk),
          })
        );
      } catch {
        if (requestIdRef.current !== requestId) return;
        dispatch({ type: 'SEARCH_ERROR', error: i18n._(GENERIC_SEARCH_ERROR) });
      }
    },
    [i18n]
  );

  const reset = useCallback((): void => {
    dispatch({ type: 'RESET' });
  }, []);

  return { state, search, reset };
}

export { getQueryNumberInSession };
